import { streamResponseObservable } from './stream-response-observable';
/**
 * Async generator wrapper for streamResponseObservable that yields partial content chunks as they arrive
 *
 * @param endpoint Which famlyapi endpoint to send the request to
 * @param requestOptions Options object to customise the fetch request
 * @param requestId Optional request identifier
 * @param abortSignal Optional AbortSignal to cancel the request
 * @yields StreamResponse objects containing partial content chunks
 *
 * @example
 * ```typescript
 * for await (const chunk of streamResponseAsAsyncGenerator('/sidekick/assist', requestOptions)) {
 *   if (chunk.content) {
 *     console.log('Received chunk:', chunk.content);
 *     // Update UI with partial content
 *   }
 *   if (chunk.stop) {
 *     console.log('Stream completed:', chunk.reason);
 *     break;
 *   }
 * }
 * ```
 */
export async function* streamResponseAsync(endpoint, requestOptions, requestId, abortSignal) {
    const chunks = [];
    let isComplete = false;
    let error = null;
    const subscription = streamResponseObservable(endpoint, requestOptions, requestId, abortSignal).subscribe({
        next: response => {
            chunks.push(response);
        },
        error: err => {
            error = err;
            isComplete = true;
        },
        complete: () => {
            isComplete = true;
        },
    });
    let index = 0;
    try {
        while (!isComplete || index < chunks.length) {
            if (error) {
                // oxlint-disable-next-line no-throw-literal
                throw error;
            }
            if (index < chunks.length) {
                const chunk = chunks[index++];
                yield chunk;
                if (chunk.stop) {
                    return;
                }
            }
            else {
                // Wait a bit before checking for new chunks
                await new Promise(resolve => setTimeout(resolve, 10));
            }
        }
    }
    finally {
        subscription.unsubscribe();
    }
}
/**
 * Helper function to consume an async generator with callbacks for streaming and completion
 *
 * @param generator The async generator to consume
 * @param onChunk Callback called for each chunk with content
 * @param onComplete Optional callback called when stream completes
 * @param onError Optional callback called when stream errors
 * @param abortSignal Optional AbortSignal to handle abort events
 * @returns Promise that resolves when stream completes
 *
 * @example
 * ```typescript
 * await consumeAsyncGenerator(
 *   functionThatWrapsStreamResponseAsync(...custom args),
 *   (content) => setRewrittenText(prev => prev + content),
 *   () => console.log('Rewrite completed')
 * );
 * ```
 */
export async function consumeAsyncGenerator(generator, onChunk, onComplete, onError, abortSignal) {
    try {
        for await (const chunk of generator) {
            // Check if aborted
            if (abortSignal === null || abortSignal === void 0 ? void 0 : abortSignal.aborted) {
                onComplete === null || onComplete === void 0 ? void 0 : onComplete('ABORTED');
                break;
            }
            if (chunk.content) {
                onChunk(chunk.content, chunk.requestId);
            }
            if (chunk.stop) {
                onComplete === null || onComplete === void 0 ? void 0 : onComplete(chunk.reason || 'DONE', chunk.requestId, chunk.message);
                break;
            }
        }
    }
    catch (error) {
        // Handle abort errors gracefully
        if (error.name === 'AbortError') {
            onComplete === null || onComplete === void 0 ? void 0 : onComplete('ABORTED');
            return;
        }
        onError === null || onError === void 0 ? void 0 : onError(error);
        throw error;
    }
}
